<!--
=========================================================
* Vue Argon Dashboard 2 - v3.0.0
=========================================================

* Product Page: https://creative-tim.com/product/vue-argon-dashboard
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
-->
<template>
  <sidenav
    v-if="store.state.showSidenav"
    class="fixed-start"
    :class="[store.state.isTransparent,]"
  />
  <main
    class="main-content position-relative max-height-vh-100 h-100"
    :class="{
      'inga-main-dark': store.state.isDarkMode,
      'border-radius-lg': !store.state.isDarkMode
    }"
  >
    <!-- nav -->
    <navbar
      v-if="store.state.showNavbar"
      :class="[navClasses]"
      :textWhite="
        store.state.isAbsolute ? 'text-white opacity-8' : 'text-white'
      "
      :minNav="navbarMinimize"
    />
    <router-view />
    <!-- footer -->
    <app-footer v-show="store.state.showFooter" />

    <!-- Global modals -->
    <auth-modal v-if="store.state.showAuthModal" />
    <base-modal v-if="store.state.modal.isOpen && (
      store.state.modal.view === 'RewardDetail' ||
      store.state.modal.view === 'GameDetail' ||
      store.state.modal.view === 'AddVoucher' ||
      store.state.modal.view === 'AddToGame' ||
      store.state.modal.view === 'TopUpCredit' ||
      store.state.modal.view === 'WithdrawCredit')"
    >
      <reward-detail-card v-if="store.state.modal.view === 'RewardDetail'" />
      <game-detail v-if="store.state.modal.view === 'GameDetail'" />
      <add-voucher-card v-if="store.state.modal.view === 'AddVoucher'"/>
      <card-register-to-game v-if="store.state.modal.view === 'AddToGame'" />
      <payment-qr-code-card
        v-if="store.state.modal.view === 'TopUpCredit'"
        :payment-data="store.state.modal.data"
      />
      <withdraw-credit-card
        v-if="store.state.modal.view === 'WithdrawCredit'"
        :with-draw-data="store.state.modal.data"
        @withdraw-credit-success="store.commit('CREDIT_REFRESH')"
      />
    </base-modal>

    <HelpDeskFormDialog v-if="store.state.showHelpDeskDialog" />
    <ForumPopup v-if="store.state.forum.isOpen" />
    <IsinChartModal />
    <RewardImageLightbox />
  </main>
</template>
<script setup>
import { computed, onBeforeMount, watch } from "vue";
import { useStore } from "vuex";
import Sidenav from "./examples/Sidenav";
import Navbar from "@/examples/Navbars/Navbar.vue";
import AppFooter from "@/examples/Footer.vue";
import AuthModal from "./views/components/custom/userAuth/AuthModal.vue";
import HelpDeskFormDialog from "./views/components/custom/HelpDeskFormDialog.vue";
import BaseModal from "@/components/BaseModal.vue";
import GameDetail from "./views/components/custom/GameDetail.vue";
import RewardDetailCard from "./views/components/custom/RewardDetailCard.vue";
import AddVoucherCard from "./views/components/custom/AddVoucherCard"
import CardRegisterToGame from "./views/components/custom/CardRegisterToGame.vue";
import PaymentQrCodeCard from "./views/components/custom/PaymentQRCodeCard.vue";
import WithdrawCreditCard from "./views/components/custom/WithdrawCreditCard.vue";
import ForumPopup from "./views/components/custom/ForumPopup.vue";
import IsinChartModal from "./views/components/custom/IsinChartModal.vue";
import RewardImageLightbox from "./views/components/custom/RewardImageLightbox.vue";

const store = useStore();

const navClasses = computed(() => {
  return {
    "position-sticky left-auto top-2 z-index-sticky": store.state.isNavFixed,
    "bg-white": store.state.isNavFixed && !store.state.isDarkMode,
    "position-absolute px-4 mx-0 w-100 z-index-2": store.state.isAbsolute,
    "px-0 mx-4": store.state.isAbsolute
  };
})

const navbarMinimize = () => { store.commit('navbarMinimize') }

const applyShellTheme = (dark) => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("inga-theme-dark", !!dark);
  document.documentElement.style.backgroundColor = dark ? "#1E293B" : "";
  document.body.classList.toggle("inga-theme-dark", !!dark);
  const appEl = document.getElementById("app");
  if (appEl) {
    appEl.classList.toggle("inga-theme-dark", !!dark);
  }
};

watch(() => store.state.isDarkMode, applyShellTheme, { immediate: true });

onBeforeMount(() => {
  store.state.isTransparent = "bg-transparent";
  window.addEventListener('beforeunload', (event) => { 
    event.preventDefault(); 
    if(store.getters.getUser) store.dispatch('logout');
  });
});

</script>

<style>
body.inga-theme-dark,
body.inga-theme-dark.bg-white-2,
#app.inga-theme-dark {
  background-color: #1E293B !important;
}

.main-content.inga-main-dark {
  background-color: #1E293B !important;
}

body:not(.inga-theme-dark),
body:not(.inga-theme-dark).bg-white-2,
#app:not(.inga-theme-dark) {
  background-color: #FFF7ED !important;
}

body:not(.inga-theme-dark) .main-content {
  background-color: #FFF7ED !important;
}

.inga-top-wash {
  background: #FFF7ED !important;
}

body:not(.inga-theme-dark) .bg-light-moniex {
  background-color: #FFF7ED !important;
}

body:not(.inga-theme-dark) .main-content > .container-fluid {
  background-color: #FFF7ED !important;
}

/* Plovoucí menu je 255px + 20px okraj. Obsah začíná hned za ním —
   zbývá jen padding stránky (1.5rem), stejný jako mezera mezi sloupci. */
@media (min-width: 1200px) {
  .sidenav.fixed-start + .main-content {
    margin-left: calc(255px + 20px) !important;
  }
}

.navbar-main {
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
}

.main-content > .container-fluid {
  padding-top: 0.75rem !important;
}

.main-content > .container-fluid.has-isin-info {
  padding-top: calc(0.75rem * 0.36125) !important;
}

/* Samsung Galaxy A54: CSS viewport 384×832 (panel 1080×2340) */
@media (max-width: 430px) {
  html,
  body,
  #app {
    overflow-x: hidden;
    max-width: 100%;
  }

  .navbar-main {
    margin-right: 0.5rem !important;
    margin-left: 0 !important;
  }

  .main-content > .container-fluid {
    padding-left: 0.6rem !important;
    padding-right: 0.6rem !important;
  }
}

/* Popupy: clona tmavší než stránka, panel světlejší šedá, ať nezanikají */
body.inga-theme-dark .modal.d-block,
.inga-modal-overlay-dark {
  background: rgba(2, 6, 23, 0.78) !important;
}

body.inga-theme-dark .modal.d-block .modal-dialog,
.inga-modal-overlay-dark .modal-dialog {
  pointer-events: auto;
  position: relative;
  z-index: 1056;
}

body.inga-theme-dark .modal-content,
.inga-modal-panel-dark {
  background: #334155 !important;
  background-color: #334155 !important;
  color: #E2E8F0 !important;
  border: 1px solid #94A3B8 !important;
  box-shadow: 0 28px 64px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 138, 76, 0.2) !important;
  pointer-events: auto !important;
}

body.inga-theme-dark .modal-content .card,
body.inga-theme-dark .modal-content .card-plain,
body.inga-theme-dark .modal-content .card-header,
body.inga-theme-dark .modal-content .card-body,
body.inga-theme-dark .modal-content .card-footer {
  background: transparent !important;
  color: #E2E8F0 !important;
}

body.inga-theme-dark .modal-content h4,
body.inga-theme-dark .modal-content h5,
body.inga-theme-dark .modal-content h6,
body.inga-theme-dark .modal-content p,
body.inga-theme-dark .modal-content .text-dark {
  color: #E2E8F0 !important;
}

body.inga-theme-dark .modal-content .form-control {
  background: #1E293B !important;
  border-color: #64748B !important;
  color: #E2E8F0 !important;
}

body.inga-theme-dark .modal-content .form-control:focus {
  background: #475569 !important;
  border-color: #FF8A4C !important;
  color: #FFFFFF !important;
  box-shadow: 0 0 0 3px rgba(255, 138, 76, 0.28) !important;
}

body.inga-theme-dark .modal-content .form-control::placeholder {
  color: #94A3B8 !important;
}

body.inga-theme-dark .otp-input {
  background-color: #1E293B !important;
  color: #E2E8F0 !important;
  border: 1px solid #64748B !important;
}

body.inga-theme-dark .otp-input:focus {
  border-color: #FF8A4C !important;
  background-color: #0F172A !important;
}

body.inga-theme-dark .modal-content .btn-primary {
  color: #ffffff !important;
  pointer-events: auto;
}

body.inga-theme-dark .swal2-container {
  background: rgba(2, 6, 23, 0.78) !important;
}

.inga-close-btn {
  -webkit-appearance: none !important;
  appearance: none !important;
  display: inline-block;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  color: #94a3b8 !important;
  font-size: 1.75rem !important;
  font-weight: 800 !important;
  line-height: 1 !important;
  padding: 0 4px !important;
  margin: 0 !important;
  min-width: 0 !important;
  width: auto !important;
  height: auto !important;
  cursor: pointer;
  text-decoration: none !important;
}

.modal-header .inga-close-btn,
.modal-header button.inga-close-btn {
  -webkit-appearance: none !important;
  appearance: none !important;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

.inga-close-btn:hover {
  color: #334155;
}

body.inga-theme-dark .inga-close-btn:hover,
.inga-close-btn-dark:hover {
  color: #E2E8F0;
}

.forum-close {
  font-weight: 800;
}

#authModal {
  z-index: 2300 !important;
}

body.inga-theme-dark .swal2-popup {
  background: #334155 !important;
  color: #E2E8F0 !important;
  border: 1px solid #94A3B8 !important;
  box-shadow: 0 28px 64px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 138, 76, 0.2) !important;
}

body.inga-theme-dark .swal2-title,
body.inga-theme-dark .swal2-html-container,
body.inga-theme-dark .swal2-html-container h5,
body.inga-theme-dark .swal2-html-container h4,
body.inga-theme-dark .swal2-html-container h6,
body.inga-theme-dark .swal2-html-container p {
  color: #E2E8F0 !important;
}

body.inga-theme-dark .swal2-icon {
  border-color: #94A3B8 !important;
}

body.inga-theme-dark .swal2-icon.swal2-error {
  border-color: #F87171 !important;
  color: #F87171 !important;
}

body.inga-theme-dark .swal2-icon.swal2-success {
  border-color: #34D399 !important;
  color: #34D399 !important;
}

body.inga-theme-dark .swal2-styled.swal2-confirm {
  background: linear-gradient(135deg, #FF7A00 0%, #FF4D00 100%) !important;
  color: #ffffff !important;
}
</style>

